import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Button } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import dayjs from 'dayjs';

import * as taxApi from '../../lib/api/taxbill';
import TaxbillList from '../../components/taxbillmng/TaxbillList';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import {
  listBondsDepts,
  setBondsDeptsFilters,
} from '../../modules/taxbillModule/bonds-depts';
import { BondsDeptsListBox } from '../../components/bonds_depts/BondsDeptsListBox';
import BondsDeptsStatusList from '../../components/bonds_depts/BondsDeptsStatusList';
import BondDeptModal from '../../components/bonds_depts/BondDeptModal';
import TaxbillTotalSummary from '../../components/taxbillmng/TaxbillTotalSummary';

const BondsDeptsListContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectRow] = useState(null);
  const handleOpenModal = (row) => {
    setOpenModal(true);
    setSelectRow(row);
  };

  const handleClose = () => {
    setOpenModal(false);
    setSelectRow(null);
  };

  const { totalPrice, bonds_depts, defaultFilters, error, loading } =
    useSelector(({ taxbillModule, loading }) => ({
      totalPrice: taxbillModule.bondsDepts.totalPrice,
      bonds_depts: taxbillModule.bondsDepts.bonds_depts,
      defaultFilters: taxbillModule.bondsDepts.defaultFilters,
      error: taxbillModule.bondsDepts.error,
      loading: loading['taxbill/LIST_BONDS_DEPTS'],
    }));

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      invTyp: filters.searchInvTyp,
      searchTyp: filters.searchTyp,
      reqCompYn: filters.searchYn,
      procCompYn: filters.procCompYn,
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listBondsDepts(makeFilters(defaultFilters)));
  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (bonds_depts) {
      setTableData(bonds_depts);
    }
  }, [bonds_depts]);

  const handleFilters = useCallback(
    (name, value) => {
      const newFilters = { ...defaultFilters, [name]: value };

      dispatch(setBondsDeptsFilters(newFilters));
    },
    [defaultFilters, dispatch],
  );

  const handleSave = async (data, msg) => {
    try {
      const response = await taxApi.onCollectBuyPay(data);
      if (response.data.info) {
        enqueueSnackbar(`${msg}되었습니다`, {
          variant: 'success',
        });
        dispatch(listBondsDepts(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const columns = useMemo(
    () => [
      { field: 'id', hide: true, sortable: false, editable: false },
      { field: 'invUuid', hide: true, sortable: false, editable: false }, //세금계산서ID
      { field: 'roUuid', hide: true, sortable: false, editable: false }, //수주ID
      { field: 'billUuid', hide: true, sortable: false, editable: false }, //거래ID
      { field: 'prjUuid', hide: true, sortable: false, editable: false }, //프로젝트ID
      {
        field: 'invYn',
        hide: true,
        editable: false,
      },
      {
        field: 'invNo',
        headerName: '계산서번호',
        hide: true,
        editable: false,
      },
      {
        field: 'invTyp',
        headerName: '유형',
        hide: true,
        editable: false,
      },

      {
        field: 'reqCompDt',
        headerName: '발행일자',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'string',
        flex: 0.8,
        minWidth: 150,
      },
      {
        field: 'procPlanDt',
        headerName: '지급(수금)예정일자',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 180,
      },
      {
        field: 'procPlanYn',
        headerName: '지급(수금)예정완료여부',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 180,
      },
      {
        field: 'rsltCompDt',
        headerName: '실적일자',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'string',
        flex: 0.8,
        minWidth: 150,
      },
      {
        field: 'action',
        headerName: 'action',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Button
            size="small"
            variant="contained"
            color={params.row.invTyp === '매출' ? 'success' : 'secondary'}
            onClick={() => handleOpenModal(params.row)}
          >
            {params.row.invTyp === '매출' ? '수금' : '지급'}
          </Button>
        ),
      },
      {
        field: 'partnerNm',
        headerName: '고객명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      
      {
        field: 'invTxt',
        headerName: '발급내용',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'invAmt',
        headerName: '계산서 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },
      {
        field: 'invVat',
        headerName: 'VAT 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },
      {
        field: 'invTot',
        headerName: '합계 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },
      {
        field: 'osTran',
        headerName: '해외거래',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'reqPlanUsrNm',
        headerName: '발행요청자',
        description: '발행요청자',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
     
      {
        field: 'salesUsrNm',
        headerName: '영업 사원',
        description: '영업 사원',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
    ],
    [],
  );

  //검색 관련 사항
  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      const searchData = {
        searchInvTyp: defaultFilters.searchInvTyp,
        searchTyp: defaultFilters.searchTyp,
        searchYn: defaultFilters.searchYn,
        procCompYn: defaultFilters.procCompYn,
        searchKeyword: values.searchKeyword,
        searchWords: values.searchWords,
        searchStrtDt: values.searchStrtDt,
        searchEndDt: values.searchEndDt,
      };
      dispatch(setBondsDeptsFilters(searchData));
      setTimeout(() => {
        setSubmitting(false);
      }, 1000);
    },
  });

  return (
    <>
      <BreadcrumbsContainer
        title="채권&채무 관리/채권&채무 관리/채권&채무 리스트"
        icon="bonds_dept"
      />
      <BondsDeptsListBox formik={formik} />
      <BondsDeptsStatusList
        filters={defaultFilters}
        onFilters={handleFilters}
      />
      <TaxbillTotalSummary totalPrice={totalPrice} />
      <TaxbillList
        columns={columns}
        rows={tableData}
        error={error}
        loading={loading}

        columnVisibilityModel={{
          id: false,
          invUuid: false,
          roUuid: false,
          billUuid: false,
          prjUuid: false,
          invYn: false,
          invTyp: false,
        }}

      />

      <BondDeptModal
        open={openModal}
        data={selectedRow}
        handleClose={handleClose}
        onSave={handleSave}
      />
    </>
  );
};

export default BondsDeptsListContainer;
